import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import MUIGrid from "@mui/material/Grid";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIAddIcon from "@mui/icons-material/Add";

import Card from "../../Card";
import Location from "../../../Utility/Crud/Location";
import Product from "../../../Utility/Crud/Product";
import ProductType from "../../../Utility/Crud/ProductType";
import ProductSubType from "../../../Utility/Crud/ProductSubType";

import Patient from "../../../Utility/Crud/Patient";
import OrderStatus from "../../../Utility/Crud/OrderStatus";
import OrderStatusReason from "../../../Utility/Crud/OrderStatusReason";

/**
 * CardChartCreateOrder renders a Card component
 * displaying create new or re-supply orderinformation.
 * @returns {JSX.Element}
 */
const className = "CardChartCreateOrder";
const title = "Create Order";
const icon = MUIAddIcon;

export default function CardChartCreateOrder(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);

    const [cardChart, setCardChart] = useState({});
    const [selectedProductType, setSelectedProductType] = useState(null);
    const [selectedProductOption, setSelectedProductOption] = useState(null);
    const [mostRecentlyAddedProductId, setMostRecentlyAddedProductId] = useState(null);

    const [defaultSalesLocation, setDefaultSalesLocation] = useState(null);
    const [defaultServiceLocation, setDefaultServiceLocation] = useState(null);

    const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);
    const [selectedOrderStatusReason, setSelectedOrderStatusReason] = useState(null);

    // Create pages should only bind to a create negative id. While the order is
    // being prepared, fall back to -1 instead of reusing a previously viewed order id.
    const newActivityId = chartContext.orderId < 0 ? chartContext.orderId : -1;

    const order = chartContext.chart?.orderActivity[newActivityId] || null;

    // Register validation rules against the actual order's id (not the
    // hard-coded -1 fallback) and only after the order exists. Otherwise the
    // rules can reference an orderActivity id that does not exist in the
    // chart, which causes Validator.validate to receive undefined and crash
    // with "Cannot read properties of undefined".
    useEffect(() => {
        if (!order) {
            chartContext.updateValidationRules({ key: className, rules: [] });
            return;
        }

        if (chartContext.pageMode === "resupply") {
            chartContext.updateValidationRules({
                key: className,
                rules: [
                    {
                        target: "orderActivity",
                        id: order.id,
                        field: "status",
                        rule: { "required": true }
                    },
                    {
                        target: "orderActivity",
                        id: order.id,
                        field: "orderStatusReasonId",
                        rule: { "required": true }
                    },
                ]
            });
        } else if (chartContext.pageMode === "new") {
            chartContext.updateValidationRules({
                key: className,
                rules: [
                    {
                        target: "orderActivity",
                        id: order.id,
                        field: "salesLocationId",
                        rule: { "required": true }
                    },
                    {
                        target: "orderActivity",
                        id: order.id,
                        field: "serviceLocationId",
                        rule: { "required": true }
                    },
                ]
            });
        } else {
            chartContext.updateValidationRules({ key: className, rules: [] });
        }
    }, [chartContext.pageMode, order?.id]);

    useEffect(() => {
        // Wait until the create-order flow has populated the real orderActivity
        // for this draft. Without this guard we would write to a hard-coded
        // newActivityId of -1 (the fallback above) before the real order exists,
        // creating a phantom orderActivity entry containing only the default
        // location fields while the real order ends up under a different
        // negative id from getNegativeId().
        if (!order) {
            return;
        }

        const location = Patient.getLocation(chartContext.chart, mainContext.locationsIndexed);

        const defaultSalesLocationValue = location
            ? getLocationOptions().find(option => option.value === location.id) || null
            : null;

        const defaultServiceLocationValue = location
            ? getLocationOptions().find(option => option.value === location.id) || null
            : null;

        setDefaultSalesLocation(defaultSalesLocationValue);
        setDefaultServiceLocation(defaultServiceLocationValue);

        const updates = {};
        if (defaultSalesLocationValue) {
            updates.salesLocationId = defaultSalesLocationValue.value;
            updates.billingLocationId = resolveBillingLocationId(defaultSalesLocationValue.value);
        }
        if (defaultServiceLocationValue) {
            updates.serviceLocationId = defaultServiceLocationValue.value;
        }

        if (Object.keys(updates).length > 0) {
            chartContext.onChange({
                orderActivity: {
                    [order.id]: { ...order, ...updates }
                }
            });
            setCardChart(previousCardChart => ({ ...previousCardChart, ...updates }));
        }
    }, [order?.id]);

    const resolveBillingLocationId = (salesLocationId) => {
        if (!salesLocationId) {
            return null;
        }

        const salesLocation = mainContext.locationsIndexed[salesLocationId];

        if (!salesLocation) {
            return null;
        }

        return salesLocation.billingLocationId
            ? salesLocation.billingLocationId
            : salesLocation.id;
    };

    const getLocationOptions = () => {
        return Location.sort(Object.values(mainContext.locationsIndexed)).map(location => ({
            label: location.name,
            value: location.id
        }));
    }

    /**
     * Determines which view to render based on edit mode state.
     * @returns {JSX.Element} Either edit view or read-only view
     */
    const renderContent = () => {
        const getCategoryOptions = () => {
            return ProductType.sort(Object.values(mainContext.productTypesIndexed)).map(productType => ({
                label: productType.name,
                value: productType.id
            }))
        }

        const getProductOptions = () => {
            const productSubTypes = ProductSubType.sort(Object.values(mainContext.productSubTypesIndexed));
            const filteredSubTypes = selectedProductType
                ? productSubTypes.filter(productSubType => productSubType.productTypeId === selectedProductType.value)
                : productSubTypes;

            return filteredSubTypes.map((productSubType, index) => ({
                label: productSubType.name + (productSubType.hcpcs ? ` - ${productSubType.hcpcs}` : ''),
                value: productSubType.id,
                key: index,// Use ID as key since it should be unique and we have multiple dublicate products
            }));
        }
        const getOrderStatusOptions = () => {
            const orderStatuses = Object.values(mainContext.orderStatusesIndexed);
            const filteredStatuses = selectedOrderStatusReason
                ? orderStatuses.filter(orderStatus => orderStatus.id === selectedOrderStatusReason.orderStatus)
                : orderStatuses;

            return OrderStatus.sort(filteredStatuses).map(orderStatus => ({
                label: orderStatus.name,
                value: orderStatus.id
            }));
        }

        const getOrderStatusReasonOptions = () => {
            const orderStatusReasons = Object.values(mainContext.orderStatusReasonsIndexed);

            const filteredReasons = selectedOrderStatus
                ? orderStatusReasons.filter(orderStatusReason => orderStatusReason.orderStatus === selectedOrderStatus.value)
                : orderStatusReasons;

            return OrderStatusReason.sort(filteredReasons).map((orderStatusReason, index) => ({
                label: orderStatusReason.description,
                value: orderStatusReason.id,
                orderStatus: orderStatusReason.orderStatus,
                key: index,// Use ID as key since it should be unique and we have multiple dublicate products
            }));
        }

        const buildProductEntry = (productSubType, newProductId) => {
            const selectedProductSubType = mainContext.productSubTypesIndexed[productSubType.value];

            return {
                id: newProductId,
                abnRequired: false,
                approved: false,
                hcpcs: selectedProductSubType.hcpcs || "",
                productSubTypeId: productSubType.value,
                productTypeId: selectedProductSubType.productTypeId,
                deliverable: true,
                billable: true,
                qty: 1,
                points: Product.getPoints(selectedProductSubType, order.insuranceTypeId, mainContext.insuranceMatrixEntriesIndexed, mainContext.productMatrixEntriesIndexed),
                serial: "",
                amount: Product.getAmount(
                    selectedProductSubType.hcpcs || "",
                    false,
                    mainContext.hcpcsCodesIndexed,
                    mainContext.feeSchedulesIndexed,
                ) ?? null,
                isRental: false,
                upgradedFromProductId: null,
            };
        };

        /**
         * Handles changes to the product dropdown selection. Removes the
         * previously added product (if any) before adding the new selection,
         * so changing the selection replaces rather than appends, and clearing
         * removes the product from the order entirely.
         * @param {Object|null} newProductOption - The newly selected option, or null when cleared.
         */
        const handleProductSelectionChange = (newProductOption) => {
            const productChanges = {};
            const orderActivityProductChanges = {};

            if (mostRecentlyAddedProductId !== null) {
                productChanges[mostRecentlyAddedProductId] = undefined;
                orderActivityProductChanges[mostRecentlyAddedProductId] = undefined;
            }

            let newProductId = null;
            if (newProductOption) {
                newProductId = chartContext.getNegativeId();
                productChanges[newProductId] = buildProductEntry(newProductOption, newProductId);
                orderActivityProductChanges[newProductId] = {
                    orderActivityId: newActivityId,
                    productsId: newProductId,
                };
            }

            if (Object.keys(productChanges).length > 0 || Object.keys(orderActivityProductChanges).length > 0) {
                chartContext.onChange({
                    product: productChanges,
                    orderActivityProducts: orderActivityProductChanges,
                });
            }

            setSelectedProductOption(newProductOption || null);
            setMostRecentlyAddedProductId(newProductId);
        }

        return (
            <>
                <MUIGrid container spacing={2} rowSpacing={2}>
                    <MUIGrid item xs={5}>
                        <MUIAutocomplete
                            options={getLocationOptions()}
                            value={defaultSalesLocation}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                const salesLocationId = newValue?.value || null;
                                const billingLocationId = resolveBillingLocationId(salesLocationId);
                                chartContext.onChange({ orderActivity: { [newActivityId]: { ...order, salesLocationId: newValue?.value || null,  billingLocationId: billingLocationId } } })
                                setCardChart({ ...cardChart, salesLocationId: newValue?.value || null })
                                setDefaultSalesLocation(newValue || null);
                            }}
                            disableClearable
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Sales Location"
                                    fullWidth
                                    required={true}
                                    error={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].salesLocationId ? true : false}
                                    helperText={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].salesLocationId ? chartContext.validationIssues[className]?.orderActivity?.[newActivityId].salesLocationId : ""}
                                />
                            )}
                        />
                    </MUIGrid>
                    <MUIGrid item xs={5}>
                        <MUIAutocomplete
                            options={getLocationOptions()}
                            value={defaultServiceLocation}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                chartContext.onChange({ orderActivity: { [newActivityId]: { ...order, serviceLocationId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, serviceLocationId: newValue?.value || null })
                                setDefaultServiceLocation(newValue || null);
                            }}
                            disableClearable
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Service Location"
                                    fullWidth
                                    required={true}
                                    error={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].serviceLocationId ? true : false}
                                    helperText={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].serviceLocationId ? chartContext.validationIssues[className]?.orderActivity?.[newActivityId].serviceLocationId : ""}
                                />
                            )}
                        />
                    </MUIGrid>
                    {chartContext.pageMode === "new" ? (
                        <>
                            <MUIGrid item xs={5}>
                                <MUIAutocomplete
                                    options={getCategoryOptions()}
                                    onChange={(e, newValue) => {
                                        setSelectedProductType(newValue || null);
                                    }}
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={selectedProductType}
                                    renderInput={(params) => (
                                        <MUITextField
                                            {...params}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            variant="standard"
                                            label="Category"
                                            fullWidth
                                        />
                                    )}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={5}>
                                <MUIAutocomplete
                                    options={getProductOptions()}
                                    value={selectedProductOption}
                                    onChange={(e, newValue) => {
                                        handleProductSelectionChange(newValue);
                                    }}
                                    getOptionLabel={option => option.label || ''}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    getOptionKey={option => option.key} // Must use this when we have multiple dublicate products to prevent console warnings
                                    renderInput={params => (
                                        <MUITextField
                                            {...params}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            variant="standard"
                                            label="Product"
                                            fullWidth
                                        />
                                    )}
                                />
                            </MUIGrid>
                        </>
                    ) : null}
                    {chartContext.pageMode === "resupply" ? (
                        <>
                            <MUIGrid item xs={5}>
                                <MUIAutocomplete
                                    options={getOrderStatusOptions()}
                                    onChange={(e, newValue) => {
                                        setSelectedOrderStatus(newValue || null);
                                        const statusValue = newValue?.value !== undefined ? newValue.value : null; // Fix for reading id of 0 (new) as null
                                        chartContext.onChange({ orderActivity: { [newActivityId]: { ...order, status: statusValue } } })
                                        setCardChart({ ...cardChart, status: statusValue })
                                    }}
                                    getOptionLabel={option => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => (
                                        <MUITextField
                                            {...params}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            variant="standard"
                                            label="Order Status"
                                            fullWidth
                                            required={true}
                                            error={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].status ? true : false}
                                            helperText={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].status ? chartContext.validationIssues[className]?.orderActivity?.[newActivityId].status : ""}
                                        />
                                    )}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={5}>
                                <MUIAutocomplete
                                    options={getOrderStatusReasonOptions()}
                                    onChange={(e, newValue) => {
                                        setSelectedOrderStatusReason(newValue || null);
                                        chartContext.onChange({ orderActivity: { [newActivityId]: { ...order, orderStatusReasonId: newValue?.value || null } } })
                                        setCardChart({ ...cardChart, orderStatusReasonId: newValue?.value || null })
                                    }}
                                    getOptionKey={option => option.key} // Must use this when we have multiple dublicate products to prevent console warnings
                                    getOptionLabel={option => option.label || ''}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={params => (
                                        <MUITextField
                                            {...params}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            variant="standard"
                                            label="Order Status Reason"
                                            fullWidth
                                            required={true}
                                            error={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].orderStatusReasonId ? true : false}
                                            helperText={chartContext.validationIssues[className]?.orderActivity?.[newActivityId].orderStatusReasonId ? chartContext.validationIssues[className]?.orderActivity?.[newActivityId].orderStatusReasonId : ""}
                                        />
                                    )}
                                />
                            </MUIGrid>
                        </>
                    ) : null}
                </MUIGrid>
            </>
        );
    }
    return (
        <Card
            renderContent={renderContent}
            getTitle={() => {
                const type = chartContext.pageMode || '';
                return type
                    .split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }}

        />
    );
}

CardChartCreateOrder.meta = { className: className, title: title, icon: icon };
